import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import {
  FiFeather,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiSearch,
  FiGrid,
  FiList,
} from "react-icons/fi";
import {
  MdLocalFireDepartment,
  MdFilterList,
  MdClear,
  MdBrandingWatermark,
  MdCategory,
} from "react-icons/md";
import {
  FaGift,
  FaRupeeSign,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { BiRupee } from "react-icons/bi";
import api from "../services/api";
import "../styles/CollectionPage.css";
import { BASE_URL } from "../config";

/* ─── Constants ─────────────────────────────────────────────────── */

const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: Low → High" },
  { value: "price-desc", label: "Price: High → Low" },
  { value: "name-asc", label: "Name: A–Z" },
  { value: "name-desc", label: "Name: Z–A" },
  { value: "newest", label: "Newest First" },
];

const BANNER_IMAGES = {
  Lungies:
    "/assets/banlu.png",
  Bedsheets:
    "/assets/banbe.png",
  Kaavi:
    "/assets/bankav.png",
  Towel:
    "/assets/banto.png"
};

const SUBCATEGORY_NAMES = {
  "premium-printed-lungies": "Premium Printed Lungies",
  "checked-lungies": "Checked Lungies",
  "kaavi-collection": "Kaavi Collection",
  bedsheets: "Bedsheets",
  "chennimalai-bedsheets": "Chennimalai Bedsheets",
  "towel-collection": "Towel Collection",
  "hometex-collection": "Hometex Collection",
};

const CATEGORY_SUBCATEGORIES = {
  Lungies: [
    { slug: "premium-printed-lungies", name: "Premium Printed Lungies" },
    { slug: "checked-lungies", name: "Checked Lungies" },
  ],
  Kaavi: [{ slug: "kaavi-collection", name: "Kaavi Collection" }],
  Bedsheets: [
    { slug: "bedsheets", name: "Bedsheets" },
    { slug: "chennimalai-bedsheets", name: "Chennimalai Bedsheets" },
  ],
  Towel: [{ slug: "towel-collection", name: "Towel Collection" }],
  Hometex: [{ slug: "hometex-collection", name: "Hometex Collection" }],
};

const MOOD_OPTIONS = [
  {
    id: "soft",
    icon: <FiFeather />,
    label: "Soft & Cozy",
    description: "Light, breathable, everyday comfort",
    tags: ["cotton", "soft"],
    maxPrice: 800,
  },
  {
    id: "bold",
    icon: <MdLocalFireDepartment />,
    label: "Bold & Bright",
    description: "Vivid colours, statement pieces",
    tags: ["coloured", "printed"],
    maxPrice: "",
  },
  {
    id: "gifting",
    icon: <FaGift />,
    label: "Gift-Ready",
    description: "Premium quality, elegant finish",
    tags: ["premium", "gift"],
    minPrice: 500,
  },
  {
    id: "budget",
    icon: <BiRupee />,
    label: "Best Value",
    description: "Great quality under ₹500",
    tags: [],
    maxPrice: 500,
  },
];

const HISTOGRAM_BUCKETS = [2, 5, 9, 14, 18, 22, 16, 11, 7, 4];
const ITEMS_PER_PAGE = 8;
const MAX_RECENT = 6;

/* ─── Session helpers ──────────────────────────────────────────── */
function getRecentlyViewed() {
  try {
    return JSON.parse(sessionStorage.getItem("rv_products") || "[]");
  } catch {
    return [];
  }
}
function addRecentlyViewed(product) {
  if (!product?._id) return;
  const prev = getRecentlyViewed().filter((p) => p._id !== product._id);
  sessionStorage.setItem(
    "rv_products",
    JSON.stringify([product, ...prev].slice(0, MAX_RECENT)),
  );
}

/* ─── Price Histogram ──────────────────────────────────────────── */
function PriceHistogram({
  buckets,
  priceMin,
  priceMax,
  minBound = 0,
  maxBound = 5000,
}) {
  const maxCount = Math.max(...buckets, 1);
  return (
    <div className="price-histogramcol" aria-label="Price distribution chart">
      <div className="histogram-barscol">
        {buckets.map((count, i) => {
          const bucketMin =
            minBound + (i / buckets.length) * (maxBound - minBound);
          const bucketMax =
            minBound + ((i + 1) / buckets.length) * (maxBound - minBound);
          const inRange =
            (!priceMin || bucketMax >= Number(priceMin)) &&
            (!priceMax || bucketMin <= Number(priceMax));
          return (
            <div
              key={i}
              className={`histogram-barcol ${inRange ? "in-range" : "out-range"}`}
              style={{ height: `${(count / maxCount) * 48}px` }}
              title={`₹${Math.round(bucketMin)}–₹${Math.round(bucketMax)}: ${count} items`}
            />
          );
        })}
      </div>
      <div className="histogram-labelscol">
        <span>₹{minBound}</span>
        <span>₹{maxBound.toLocaleString()}</span>
      </div>
    </div>
  );
}

/* ─── Active Filter Pills ───────────────────────────────────────── */
function ActiveFilters({
  checkedBrands,
  checkedCats,
  priceMin,
  priceMax,
  activeMood,
  onRemoveBrand,
  onRemoveCat,
  onClearPrice,
  onClearMood,
  onClearAll,
}) {
  const pills = [
    ...checkedCats.map((c) => ({
      id: `cat:${c}`,
      label: c,
      onRemove: () => onRemoveCat(c),
    })),
    ...checkedBrands.map((b) => ({
      id: `brand:${b}`,
      label: b,
      onRemove: () => onRemoveBrand(b),
    })),
    ...(priceMin || priceMax
      ? [
          {
            id: "price",
            label: `₹${priceMin || "0"} – ₹${priceMax || "∞"}`,
            onRemove: onClearPrice,
          },
        ]
      : []),
    ...(activeMood
      ? [
          {
            id: "mood",
            label: MOOD_OPTIONS.find((m) => m.id === activeMood)?.label,
            onRemove: onClearMood,
          },
        ]
      : []),
  ];
  if (!pills.length) return null;
  return (
    <div className="active-filters-barcol">
      <span className="active-filters-labelcol">Active filters:</span>
      {pills.map((p) => (
        <button
          key={p.id}
          className="active-filter-pillcol"
          onClick={p.onRemove}
        >
          {p.label} <span className="pill-xcol">×</span>
        </button>
      ))}
      <button className="active-filters-clearcol" onClick={onClearAll}>
        Clear all
      </button>
    </div>
  );
}

/* ─── Recently Viewed Rail ──────────────────────────────────────── */
function RecentlyViewedRail({ products, onNavigate }) {
  if (!products.length) return null;
  return (
    <div className="recently-viewed-railcol">
      <p className="recently-viewed-titlecol">Recently Viewed</p>
      <div className="recently-viewed-scrollcol">
        {products.map((p) => (
          <div
            key={p._id}
            className="recently-viewed-cardcol"
            onClick={() => onNavigate(p)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onNavigate(p)}
          >
            <div className="rv-img-wrapcol">
              <img
                src={`${BASE_URL}${p.images?.[0] || p.image}`}
                alt={p.name}
                loading="lazy"
                onError={(e) => {
                  e.target.src = `https://placehold.co/400x530/ece8e2/999?text=${encodeURIComponent(p.name?.substring(0, 8) || "IMG")}`;
                }}
              />
              <div
                className="rv-zoom-overlaycol"
                style={{
                  backgroundImage: `url(${p.images?.[0] || p.image})`,
                  backgroundSize: "300% 300%",
                  backgroundPosition: "center",
                }}
              />
            </div>
            <p className="rv-namecol">{p.name}</p>
            <p className="rv-pricecol">₹{p.price}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Mood Filter Bar ───────────────────────────────────────────── */
function MoodFilterBar({ activeMood, onSelectMood }) {
  return (
    <div className="mood-filter-barcol">
      <p className="mood-filter-titlecol">Shop by Mood</p>
      <div className="mood-cardscol">
        {MOOD_OPTIONS.map((m) => (
          <button
            key={m.id}
            className={`mood-cardcol ${activeMood === m.id ? "active" : ""}`}
            onClick={() => onSelectMood(activeMood === m.id ? null : m.id)}
          >
            <span className="mood-iconcol">{m.icon}</span>
            <span className="mood-labelcol">{m.label}</span>
            <span className="mood-desccol">{m.description}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Subcategory Tabs ──────────────────────────────────────────── */
function SubcategoryTabs({ category, activeSlug, onTabChange }) {
  const subcategories = CATEGORY_SUBCATEGORIES[category] || [];
  if (subcategories.length <= 1) return null;
  return (
    <div className="subcategory-tabs">
      <div className="tabs-container">
        {subcategories.map((sub) => (
          <button
            key={sub.slug}
            className={`tab-btn ${activeSlug === sub.slug ? "active" : ""}`}
            onClick={() => onTabChange(sub.slug, sub.name)}
          >
            {sub.name}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Skeleton Card ─────────────────────────────────────────────── */
function SkeletonCard({ list }) {
  return (
    <div className={`skeleton-card ${list ? "skeleton-list" : ""}`}>
      <div className="skeleton-img" />
      <div className="skeleton-body">
        <div className="skeleton-line w70" />
        <div className="skeleton-line w40" />
        {list && <div className="skeleton-line w55" />}
      </div>
    </div>
  );
}

/* ─── Product Card with Fabric Zoom ────────────────────────────── */
function ProductCardWithZoom({ product, listView }) {
  const [hovered, setHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const cardRef = useRef(null);
  const productImage = product.images?.[0] || product.image;

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <div
      ref={cardRef}
      className={`product-zoom-wrappercol ${listView ? "list-view-card" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseMove={handleMouseMove}
    >
      <ProductCard product={product} listView={listView} />
      {hovered && productImage && !listView && (
        <div
          className="fabric-zoom-lenscol"
          style={{
            backgroundImage: `url(${BASE_URL}${productImage})`,
            backgroundPosition: `${mousePos.x}% ${mousePos.y}%`,
          }}
        />
      )}
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────── */
export default function CollectionPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { category, slug, search: locationSearch } = location.state || {};

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  const [sort, setSort] = useState("featured");
  const [filterOpen, setFilterOpen] = useState(false);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [checkedBrands, setCheckedBrands] = useState([]);
  const [checkedCats, setCheckedCats] = useState([]);
  const [page, setPage] = useState(1);

  const [activeSubcategorySlug, setActiveSubcategorySlug] = useState(
    slug || null,
  );
  const [activeSubcategoryName, setActiveSubcategoryName] = useState("");

  const [activeMood, setActiveMood] = useState(null);
  const [recentlyViewed, setRecentlyViewed] = useState(getRecentlyViewed);
  const [viewMode, setViewMode] = useState("grid");

  // Load filter metadata
  useEffect(() => {
    api.products
      .getCategories()
      .then((d) => {
        setBrands(d.brands || []);
        setCategories(d.categories || []);
      })
      .catch(() => {});
  }, []);

  // Sync active subcategory from navigation state
  useEffect(() => {
    if (slug) {
      setActiveSubcategorySlug(slug);
      const subs = CATEGORY_SUBCATEGORIES[category] || [];
      const found = subs.find((s) => s.slug === slug);
      setActiveSubcategoryName(
        found ? found.name : SUBCATEGORY_NAMES[slug] || slug,
      );
    } else {
      setActiveSubcategorySlug(null);
      setActiveSubcategoryName("");
    }
  }, [slug, category]);

  const handleMoodSelect = (moodId) => {
    setActiveMood(moodId);
    if (!moodId) {
      setPriceMin("");
      setPriceMax("");
      return;
    }
    const mood = MOOD_OPTIONS.find((m) => m.id === moodId);
    if (mood) {
      setPriceMax(mood.maxPrice !== undefined ? String(mood.maxPrice) : "");
      setPriceMin(mood.minPrice ? String(mood.minPrice) : "");
    }
  };

  const handleTabChange = (newSlug, newName) => {
    setActiveSubcategorySlug(newSlug);
    setActiveSubcategoryName(newName);
    setPage(1);
    navigate("/collection", {
      state: { category, slug: newSlug, subCategory: newName },
    });
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        sort,
        page,
        limit: ITEMS_PER_PAGE,
        ...(locationSearch && { search: locationSearch }),
        ...(category &&
          category !== "all" &&
          !checkedCats.length && { category }),
        ...(activeSubcategorySlug &&
          activeSubcategorySlug !== "all" && {
            subcategory: activeSubcategorySlug,
          }),
        ...(checkedBrands.length && { brand: checkedBrands.join(",") }),
        ...(checkedCats.length && { category: checkedCats.join(",") }),
        ...(priceMin && { minPrice: priceMin }),
        ...(priceMax && { maxPrice: priceMax }),
      };
      const data = await api.products.getAll(params);
      setProducts(data.products || []);
      setTotal(data.total || 0);
      if (data.products?.[0]) addRecentlyViewed(data.products[0]);
      setRecentlyViewed(getRecentlyViewed());
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [
    sort,
    page,
    locationSearch,
    category,
    activeSubcategorySlug,
    checkedBrands,
    checkedCats,
    priceMin,
    priceMax,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    sort,
    locationSearch,
    category,
    activeSubcategorySlug,
    checkedBrands,
    checkedCats,
    priceMin,
    priceMax,
  ]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const toggleBrand = (b) =>
    setCheckedBrands((p) =>
      p.includes(b) ? p.filter((x) => x !== b) : [...p, b],
    );
  const toggleCat = (c) =>
    setCheckedCats((p) =>
      p.includes(c) ? p.filter((x) => x !== c) : [...p, c],
    );

  const clearAll = () => {
    setCheckedBrands([]);
    setCheckedCats([]);
    setPriceMin("");
    setPriceMax("");
    setActiveMood(null);
    setPage(1);
  };

  const displayTitle = (() => {
    if (locationSearch) return `Search: "${locationSearch}"`;
    if (activeSubcategoryName) return activeSubcategoryName;
    if (activeSubcategorySlug && activeSubcategorySlug !== "all")
      return activeSubcategorySlug
        .replace(/-/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    return category || "All Products";
  })();

  const hasActiveFilters =
    checkedBrands.length ||
    checkedCats.length ||
    priceMin ||
    priceMax ||
    activeMood;
  const isListView = viewMode === "list";

  return (
    <div className="collection-page-root">
      <div className="container">
        {/* Breadcrumb */}
        <div className="collection-breadcrumbcol">
          <a onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            Home
          </a>
          <span>/</span>
          {category && (
            <>
              <a
                onClick={() => {
                  setActiveSubcategorySlug(null);
                  setActiveSubcategoryName("");
                  navigate("/collection", { state: { category } });
                }}
                style={{ cursor: "pointer" }}
              >
                {category}
              </a>
              <span>/</span>
            </>
          )}
          <span className="breadcrumb-current">{displayTitle}</span>
        </div>

        {/* Hero Banner */}
        <div className="collection-bannercol">
          <img
            src={BANNER_IMAGES[category] || BANNER_IMAGES.default}
            alt={displayTitle}
          />
          <div className="banner-gradientcol" />
          <div className="collection-banner-textcol">
            <h1>{displayTitle}</h1>
            <p>{loading ? "Loading…" : `${total} products`}</p>
          </div>
        </div>

        {/* Subcategory Tabs */}
        <SubcategoryTabs
          category={category}
          activeSlug={activeSubcategorySlug}
          onTabChange={handleTabChange}
        />

        {/* Mood Filter */}
        <MoodFilterBar
          activeMood={activeMood}
          onSelectMood={handleMoodSelect}
        />

        {/* Active Filter Pills */}
        {hasActiveFilters && (
          <ActiveFilters
            checkedBrands={checkedBrands}
            checkedCats={checkedCats}
            priceMin={priceMin}
            priceMax={priceMax}
            activeMood={activeMood}
            onRemoveBrand={(b) =>
              setCheckedBrands((p) => p.filter((x) => x !== b))
            }
            onRemoveCat={(c) => setCheckedCats((p) => p.filter((x) => x !== c))}
            onClearPrice={() => {
              setPriceMin("");
              setPriceMax("");
            }}
            onClearMood={() => handleMoodSelect(null)}
            onClearAll={clearAll}
          />
        )}

        <div className="collection-layoutcol">
          {/* ── Filter Sidebar ── */}
          <aside className={`filter-sidebarcol ${filterOpen ? "open" : ""}`}>
            {filterOpen && (
              <div
                className="filter-overlaycol"
                onClick={() => setFilterOpen(false)}
              />
            )}

            <div className="filter-sidebar-inner">
              <div className="filter-sidebar-header">
                <h3>
                  <MdFilterList /> Filters
                </h3>
                <button
                  className="filter-close-btn"
                  onClick={() => setFilterOpen(false)}
                >
                  ✕
                </button>
              </div>

              {categories.length > 0 && (
                <div className="filter-groupcol">
                  <div className="filter-group-titlecol">
                    <MdCategory /> Categories
                  </div>
                  {categories.map((c) => (
                    <div className="filter-optioncol" key={c}>
                      <input
                        type="checkbox"
                        id={`cat-${c}`}
                        checked={checkedCats.includes(c)}
                        onChange={() => toggleCat(c)}
                      />
                      <label htmlFor={`cat-${c}`}>{c}</label>
                    </div>
                  ))}
                </div>
              )}

              <div className="filter-groupcol">
                <div className="filter-group-titlecol">
                  <FaRupeeSign /> Price Range
                </div>
                <PriceHistogram
                  buckets={HISTOGRAM_BUCKETS}
                  priceMin={priceMin}
                  priceMax={priceMax}
                  maxBound={5000}
                />
                <div className="price-inputs-row">
                  <input
                    type="number"
                    placeholder="Min ₹"
                    value={priceMin}
                    onChange={(e) => setPriceMin(e.target.value)}
                    className="price-input"
                  />
                  <span className="price-dash">–</span>
                  <input
                    type="number"
                    placeholder="Max ₹"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                    className="price-input"
                  />
                </div>
              </div>

              {brands.length > 0 && (
                <div className="filter-groupcol">
                  <div className="filter-group-titlecol">
                    <MdBrandingWatermark /> Brand
                  </div>
                  {brands.map((b) => (
                    <div className="filter-optioncol" key={b}>
                      <input
                        type="checkbox"
                        id={`brand-${b}`}
                        checked={checkedBrands.includes(b)}
                        onChange={() => toggleBrand(b)}
                      />
                      <label htmlFor={`brand-${b}`}>{b}</label>
                    </div>
                  ))}
                </div>
              )}

              <button className="clear-filter-btn" onClick={clearAll}>
                <MdClear /> Clear All Filters
              </button>
            </div>
          </aside>

          {/* ── Main Grid ── */}
          <div className="collection-maincol">
            {/* Toolbar */}
            <div className="collection-toolbarcol">
              <button
                className="filter-toggle-btncol"
                onClick={() => setFilterOpen((o) => !o)}
              >
                <FiFilter />
                <span>Filters</span>
                {filterOpen ? <FiChevronUp /> : <FiChevronDown />}
                {hasActiveFilters ? (
                  <span className="filter-badge">
                    {[
                      checkedBrands.length,
                      checkedCats.length,
                      priceMin || priceMax ? 1 : 0,
                      activeMood ? 1 : 0,
                    ].reduce((a, b) => a + b, 0)}
                  </span>
                ) : null}
              </button>

              <span className="collection-countcol">
                {loading
                  ? "Loading…"
                  : `${Math.min((page - 1) * ITEMS_PER_PAGE + 1, total)}–${Math.min(page * ITEMS_PER_PAGE, total)} of ${total}`}
              </span>

              <div className="toolbar-right">
                <div className="view-mode-togglecol">
                  <button
                    className={`view-btncol ${viewMode === "grid" ? "active" : ""}`}
                    onClick={() => setViewMode("grid")}
                    title="Grid view"
                    aria-label="Grid view"
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 14 14"
                      fill="currentColor"
                    >
                      <rect x="0" y="0" width="6" height="6" rx="1" />
                      <rect x="8" y="0" width="6" height="6" rx="1" />
                      <rect x="0" y="8" width="6" height="6" rx="1" />
                      <rect x="8" y="8" width="6" height="6" rx="1" />
                    </svg>
                    <span>Grid</span>
                  </button>
                  <button
                    className={`view-btncol ${viewMode === "list" ? "active" : ""}`}
                    onClick={() => setViewMode("list")}
                    title="List view"
                    aria-label="List view"
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 14 14"
                      fill="currentColor"
                    >
                      <rect x="0" y="0" width="14" height="3" rx="1" />
                      <rect x="0" y="5" width="14" height="3" rx="1" />
                      <rect x="0" y="10" width="14" height="3" rx="1" />
                    </svg>
                    <span>List</span>
                  </button>
                </div>

                <select
                  className="sort-selectcol"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Products */}
            {loading ? (
              <div
                className={`products-gridcol ${isListView ? "list-view" : ""}`}
              >
                {[...Array(8)].map((_, i) => (
                  <SkeletonCard key={i} list={isListView} />
                ))}
              </div>
            ) : products.length > 0 ? (
              <div
                className={`products-gridcol ${isListView ? "list-view" : ""}`}
              >
                {products.map((p) => (
                  <ProductCardWithZoom
                    key={p._id}
                    product={p}
                    listView={isListView}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <FiSearch />
                </div>
                <h3>No products found</h3>
                <p>Try adjusting your filters or search term.</p>
                <button className="btn btn-primary" onClick={clearAll}>
                  <MdClear /> Clear Filters
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && !loading && (
              <div className="paginationcol">
                <button
                  className="page-btncol"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <FaChevronLeft />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`page-btncol ${page === i + 1 ? "active" : ""}`}
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="page-btncol"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Recently Viewed */}
        <RecentlyViewedRail
          products={recentlyViewed}
          onNavigate={(p) =>
            navigate(`/product/${p._id}`, { state: { product: p } })
          }
        />
      </div>
    </div>
  );
}
